import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { AlertCircle } from 'lucide-react'
import { Button } from '@/components/ui/button'

// ============================================================================
// Item 307 — what a Settings section shows instead of waiting on the server
// ============================================================================
// Every section draws its title and description at once; its own data area shows placeholder rows
// while loading, its own "Couldn't load — Try again" on failure, and a "Saving… / Saved" note beside a
// control that saves on its own. One slow or failed section never holds back another.

const BAR = 'rounded bg-muted animate-pulse motion-reduce:animate-none'

/** Placeholder rows in the shape of a settings card row (icon, two lines, a control), announced as loading. */
export function SectionSkeleton({ rows = 1 }: { rows?: number }) {
  const { t } = useTranslation('settings')
  return (
    <div
      role="status"
      aria-busy="true"
      aria-label={t('sectionState.loading')}
      className="divide-y rounded-lg border bg-card shadow-sm"
    >
      {Array.from({ length: rows }, (_, i) => (
        <div key={i} data-skeleton-row className="flex items-center gap-3 p-4">
          <div className={`h-8 w-8 shrink-0 rounded-full ${BAR}`} />
          <div className="min-w-0 flex-1 space-y-2">
            <div className={`h-3 w-1/3 ${BAR}`} />
            <div className={`h-3 w-2/3 ${BAR}`} />
          </div>
          <div className={`h-5 w-9 shrink-0 rounded-full ${BAR}`} />
        </div>
      ))}
    </div>
  )
}

/** A section's own load failure: "Couldn't load — Try again" (Try again refetches that section only). */
export function SectionError({ onRetry }: { onRetry: () => void }) {
  const { t } = useTranslation('settings')
  return (
    <div
      role="alert"
      className="flex items-center justify-between gap-3 rounded-lg border border-destructive/30 bg-destructive/5 px-4 py-3"
    >
      <span className="flex items-center gap-2 text-sm text-destructive">
        <AlertCircle className="h-4 w-4 shrink-0" />
        {t('sectionState.loadFailed')}
      </span>
      <Button type="button" size="sm" variant="outline" onClick={onRetry}>
        {t('sectionState.retry')}
      </Button>
    </div>
  )
}

/** How long "Saved" stays after a save. */
export const SAVED_VISIBLE_MS = 3000

/** "Saving…" while a control's save is in flight, then "Saved" for 3 s. Always mounted, so screen readers hear it. */
export function SaveStatus({ pending, saved }: { pending: boolean; saved: boolean }) {
  const { t } = useTranslation('settings')
  const [savedVisible, setSavedVisible] = useState(false)

  useEffect(() => {
    if (pending || !saved) {
      setSavedVisible(false)
      return
    }
    setSavedVisible(true)
    const timer = setTimeout(() => setSavedVisible(false), SAVED_VISIBLE_MS)
    return () => clearTimeout(timer)
  }, [pending, saved])

  return (
    <span aria-live="polite" className="shrink-0 text-xs text-muted-foreground">
      {pending ? t('sectionState.saving') : savedVisible ? t('sectionState.saved') : ''}
    </span>
  )
}
